import { Flex, Skeleton, SkeletonProps, Tooltip } from 'antd'
import cloneDeep from 'lodash-es/cloneDeep'
import head from 'lodash-es/head'
import isEmpty from 'lodash-es/isEmpty'
import toNumber from 'lodash-es/toNumber'
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import Highlighter from 'react-highlight-words'
import styled from 'styled-components'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import KolPlatformLink from '@/components/kol/common/kol-platform-link'
import { KolIntroModal } from '@/components/kol/detail/kol-intro-modal'
import genderI18nIdMap from '@/constants/i18n/gender-i18n-id-map'
import { LANGUAGE_CODE_LIST } from '@/constants/search/language-code'
import useCustomizedTag from '@/hooks/use-customized-tag'
import { useThemeConstants } from '@/hooks/use-theme-constants'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { Platform, PlatformShortcode } from '@/utils/convert-platform'
import filterOperator from '@/utils/filter-operator'

interface BasicInfoProps {
  kolInfo?: KolSchemaModel
  platform: Platform
  isFetchingKolInfo: boolean
  keyword?: string
}

const BasicInfo: FunctionComponent<BasicInfoProps> = ({
  kolInfo,
  platform,
  isFetchingKolInfo,
  keyword,
}) => {
  const filteredKeyword = filterOperator(keyword?.split(' ') ?? [])
  const { formatMessage, formatNumber } = useIntl()
  const { highlightStyle } = useThemeConstants()
  const [displayIntroModal, setDisplayIntroModal] = useState(false)
  const { getCustomizedTagLocalizedName } = useCustomizedTag()
  const descriptionRef = useRef<HTMLDivElement>(null)
  const [displaySeeMore, setDisplaySeeMore] = useState(false)

  const statisticLoading: SkeletonProps | boolean = isFetchingKolInfo
    ? {
        active: true,
        title: { width: 60 },
        paragraph: { rows: 1, width: 100 },
      }
    : false

  const { title, kolLanguages, weights } = useMemo((): Record<
    'title' | 'kolLanguages' | 'weights',
    string[]
  > => {
    const defaultValue = {
      title: [],
      kolLanguages: [],
      weights: [],
    }

    if (!kolInfo?.languages || isEmpty(kolInfo?.languages)) {
      return defaultValue
    }

    return cloneDeep(kolInfo.languages)
      .sort((a, b) => toNumber(b.weight) - toNumber(a.weight))
      .reduce(
        (
          acc: { title: string[]; kolLanguages: string[]; weights: string[] },
          { name, weight },
        ) => {
          if (!LANGUAGE_CODE_LIST[name]) {
            return acc
          }

          const langI18n = formatMessage({
            id: LANGUAGE_CODE_LIST[name].i18nKey,
          })

          const weightNumber = toNumber(weight)
          if (weightNumber === 0) {
            return acc
          }

          const formattedWeight = formatNumber(weightNumber, {
            isPercentageDisplay: true,
            percentagePrecision: 0,
          })

          acc.title.push(`${langI18n} ${formattedWeight}`)
          acc.kolLanguages.push(langI18n)
          acc.weights.push(formattedWeight)
          return acc
        },
        defaultValue,
      )
  }, [kolInfo?.languages, formatMessage, formatNumber])
  const currentPlatform = kolInfo?.links.find(
    (link) => link.platformType === platform.type,
  )
  const description = currentPlatform?.description ?? ''
  const localizedCustomizedTagsNames = kolInfo?.customizedTagsNames.map(
    getCustomizedTagLocalizedName,
  )
  const customizedNames = localizedCustomizedTagsNames?.length
    ? localizedCustomizedTagsNames.join(' · ')
    : formatMessage({
        id: 'general:empty',
      })
  const shouldShowKolLanguages =
    platform.shortcode !== PlatformShortcode.Threads

  /**
   * @description When the text overflows the -webkit-line-clamp limit,
   * display the "See More" button.
   */
  useEffect(() => {
    if (
      (descriptionRef?.current?.scrollHeight ?? 0) >
      (descriptionRef?.current?.clientHeight ?? 0)
    ) {
      setDisplaySeeMore(true)
    }
  }, [description])

  return (
    <KolDetailDataBlock
      colSpan={8}
      header={{
        title: `${platform.title} ${formatMessage({ id: 'kol:basic_info' })}`,
      }}
      kolId={kolInfo?.id}
      kolMetrics='Overview'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Wrapper>
        <StatisticsWrapper>
          <KolDetailStatistic
            loading={statisticLoading}
            title={formatMessage({ id: 'kol:edit_fans_group_gender' })}
            value={formatMessage({
              id: kolInfo?.gender
                ? genderI18nIdMap[kolInfo.gender]
                : 'autoreport:chart_label_empty_data',
            })}
          />
          {shouldShowKolLanguages && (
            <KolDetailStatistic
              loading={statisticLoading}
              suffix={
                <Tooltip placement='bottom' title={title.join('、')}>
                  <Percentage>{head(weights)}</Percentage>
                </Tooltip>
              }
              title={formatMessage({ id: 'kol:primary_language' })}
              value={isEmpty(kolLanguages) ? null : head(kolLanguages)}
            />
          )}
        </StatisticsWrapper>
        <CustomizedNamesBlock>
          {isFetchingKolInfo ? (
            <Skeleton active paragraph={{ rows: 3 }} title={false} />
          ) : (
            <>
              <KolDetailDataBlockSubHeader
                title={formatMessage({ id: 'search:result_kol_charcs_type' })}
              />
              {isEmpty(customizedNames) ? (
                <NoData>
                  {formatMessage({ id: 'autoreport:chart_label_empty_data' })}
                </NoData>
              ) : (
                <Tooltip title={customizedNames}>{customizedNames}</Tooltip>
              )}
            </>
          )}
        </CustomizedNamesBlock>
        <AboutBlock>
          {isFetchingKolInfo ? (
            <Skeleton active paragraph={{ rows: 4 }} title={false} />
          ) : (
            <Flex vertical gap={15}>
              <KolDetailDataBlockSubHeader
                title={formatMessage({ id: 'project:export_influencer_intro' })}
              />
              <KolPlatformLink
                from='description'
                kolInfo={kolInfo}
                platformShortcode={platform.shortcode}
                searchWords={filteredKeyword}
              />
              <Description ref={descriptionRef}>
                {isEmpty(description) ? (
                  <NoData>
                    {formatMessage({ id: 'autoreport:chart_label_empty_data' })}
                  </NoData>
                ) : (
                  <Highlighter
                    autoEscape
                    highlightStyle={highlightStyle}
                    searchWords={filteredKeyword}
                    textToHighlight={description}
                  />
                )}
              </Description>
              {displaySeeMore && (
                <SeeMoreWrapper>
                  <SeeMore onClick={(): void => setDisplayIntroModal(true)}>
                    {formatMessage({ id: 'search:result_see_more_btn' })}
                  </SeeMore>
                </SeeMoreWrapper>
              )}
            </Flex>
          )}
        </AboutBlock>
        <KolIntroModal
          customTags={kolInfo?.customTag}
          highlightText={filteredKeyword}
          text={
            Array.from(description).length > 1024
              ? description.slice(0, 1024) + ' ...'
              : description
          }
          toggleVisible={(): void => setDisplayIntroModal(false)}
          visible={displayIntroModal}
        />
      </Wrapper>
    </KolDetailDataBlock>
  )
}

export default BasicInfo

const Percentage = styled.span`
  color: ${({ theme }): string => theme.colors.brand.secondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  align-self: flex-end;
`

const NoData = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey4};
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const SeeMore = styled.span`
  cursor: pointer;
  text-decoration: underline;
`

const SeeMoreWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: -15px; /* remove gap */
`

const CustomizedNamesBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  span {
    color: ${({ theme }): string => theme.colors.base.grey.grey2};
    font-size: 13px;
    font-weight: 400;
    line-height: 140%;
    margin: 0;
    word-break: break-all;
  }
  ${SeeMore} {
    color: ${({ theme }): string => theme.colors.brand.primary};

    :hover {
      color: ${({ theme }): string => theme.colors.brand.primaryHover};
    }
  }
`

const AboutBlock = styled(CustomizedNamesBlock)`
  gap: 15px;

  span {
    word-break: normal;
    overflow-wrap: anywhere;
  }
`

const StatisticsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  position: relative;
  ${KolPlatformLink} {
    gap: 15px;
  }
`

const Description = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  position: relative;
`
